import { useState, useEffect, useRef, useMemo, useCallback, createContext, useContext, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { AppRole } from "@/lib/constants";

interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  community_id: string | null;
  email_consent: boolean;
  sms_consent: boolean;
}

interface UserRole {
  role: AppRole;
  community_id: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  roles: UserRole[];
  loading: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  hasRole: (role: AppRole, communityId?: string) => boolean;
  refreshRoles: () => Promise<void>;
  isSuperAdmin: boolean;
  isBusinessAdmin: boolean;
  isStaff: boolean;
  isCustomer: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchedForUserId = useRef<string | null>(null);

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const syncProfileFromMetadata = useCallback(async (currentUser: User, currentProfile: UserProfile | null) => {
    const metadataCommunityId = typeof currentUser.user_metadata?.community_id === "string"
      ? currentUser.user_metadata.community_id
      : null;
    const metadataEmailConsent = typeof currentUser.user_metadata?.email_consent === "boolean"
      ? currentUser.user_metadata.email_consent
      : null;
    const metadataSmsConsent = typeof currentUser.user_metadata?.sms_consent === "boolean"
      ? currentUser.user_metadata.sms_consent
      : null;

    if (!currentUser.email_confirmed_at || !metadataCommunityId) {
      return false;
    }

    const needsSync =
      !currentProfile ||
      currentProfile.community_id !== metadataCommunityId ||
      (metadataEmailConsent !== null && currentProfile.email_consent !== metadataEmailConsent) ||
      (metadataSmsConsent !== null && currentProfile.sms_consent !== metadataSmsConsent);

    if (!needsSync) {
      return false;
    }

    const updates: Pick<UserProfile, "community_id" | "email_consent" | "sms_consent"> = {
      community_id: metadataCommunityId,
      email_consent: metadataEmailConsent ?? currentProfile?.email_consent ?? true,
      sms_consent: metadataSmsConsent ?? currentProfile?.sms_consent ?? false,
    };

    const { error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", currentUser.id);

    if (error) {
      console.error("Error syncing signup metadata to profile:", error);
      return false;
    }

    return true;
  }, []);

  const fetchUserDataDirect = async (userId: string, accessToken: string, authUser?: User) => {
    try {
      const headers = {
        apikey: anonKey,
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      };

      const fetchProfileAndRoles = async () => {
        const [profileRes, rolesRes] = await Promise.all([
          fetch(`${supabaseUrl}/rest/v1/profiles?id=eq.${userId}&select=*&limit=1`, { headers }),
          fetch(`${supabaseUrl}/rest/v1/user_roles?user_id=eq.${userId}&select=role,community_id`, { headers }),
        ]);

        const [profileData, rolesData] = await Promise.all([
          profileRes.json(),
          rolesRes.json(),
        ]);

        return {
          profile: Array.isArray(profileData) && profileData.length > 0
            ? (profileData[0] as UserProfile)
            : null,
          roles: Array.isArray(rolesData) ? (rolesData as UserRole[]) : [],
        };
      };

      let { profile: nextProfile, roles: nextRoles } = await fetchProfileAndRoles();

      if (authUser) {
        const synced = await syncProfileFromMetadata(authUser, nextProfile);
        if (synced) {
          const refreshedData = await fetchProfileAndRoles();
          nextProfile = refreshedData.profile;
          nextRoles = refreshedData.roles;
        }
      }

      setProfile(nextProfile);
      setRoles(nextRoles);
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let safetyTimeout: ReturnType<typeof setTimeout>;

    safetyTimeout = setTimeout(() => {
      if (isMounted) {
        setLoading((current) => {
          if (current) {
            console.warn("Auth safety timeout after 12s, forcing loading=false");
            return false;
          }
          return current;
        });
      }
    }, 12000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, currentSession) => {
        if (!isMounted) return;

        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          const uid = currentSession.user.id;
          if (fetchedForUserId.current === uid) {
            return;
          }
          fetchedForUserId.current = uid;
          const token = currentSession.access_token;
          setTimeout(() => {
            if (isMounted) {
              fetchUserDataDirect(uid, token, currentSession.user);
            }
          }, 0);
        } else {
          fetchedForUserId.current = null;
          setProfile(null);
          setRoles([]);
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!isMounted) return;
      if (!s && fetchedForUserId.current === null) {
        setLoading(false);
      }
    }).catch((error) => {
      console.error("Auth getSession failed:", error);
      if (isMounted) setLoading(false);
    });

    return () => {
      isMounted = false;
      fetchedForUserId.current = null;
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, [syncProfileFromMetadata]);

  const signUp = useCallback(async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          full_name: fullName,
        },
      },
    });
    if (error) throw error;
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
    fetchedForUserId.current = null;
    // Clear salary calculator session history on logout
    try { sessionStorage.removeItem("salary_calc_history"); } catch {}
    if (error && !error.message?.toLowerCase().includes("session not found")) {
      throw error;
    }
  }, []);

  const hasRole = useCallback((role: AppRole, communityId?: string): boolean => {
    return roles.some(
      (r) =>
        r.role === role &&
        (communityId === undefined || r.community_id === communityId || r.community_id === null)
    );
  }, [roles]);

  const refreshRoles = useCallback(async () => {
    const currentSession = session;
    if (!currentSession?.user) return;
    fetchedForUserId.current = null;
    const uid = currentSession.user.id;
    const token = currentSession.access_token;
    fetchedForUserId.current = uid;
    await fetchUserDataDirect(uid, token, currentSession.user);
  }, [session]);

  const value = useMemo<AuthContextType>(() => ({
    user,
    session,
    profile,
    roles,
    loading,
    signUp,
    signIn,
    signOut,
    hasRole,
    refreshRoles,
    isSuperAdmin: hasRole("super_admin"),
    isBusinessAdmin: hasRole("business_admin"),
    isStaff: hasRole("staff"),
    isCustomer: hasRole("customer"),
  }), [user, session, profile, roles, loading, signUp, signIn, signOut, hasRole, refreshRoles]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
